import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import { useV2ListPhasesForAccountQuery } from "@generated";
import TextField from "@components/FormsV2/TextField/TextField";
import FormNumberFieldV2 from "@components/FormsV2/FormNumberFieldV2/FormNumberFieldV2";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import DropdownApi from "@components/FormsV2/DropdownApi/DropdownApi";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import "./style.css";
import { STATUS_OPTIONS } from "../types";
import type {
  ExpenseCategoryFormValues,
  ExpenseCategoryStatus,
} from "../types";

export interface ExpenseCategoryFormProps {
  id: string;
  initialValues: ExpenseCategoryFormValues;
  isSubmitting: boolean;
  onSubmit: (values: ExpenseCategoryFormValues) => void;
}

const toOption = (id: string | null, label: string | null) =>
  id == null ? undefined : { value: id, label: label ?? String(id) };

export const ExpenseCategoryForm = ({
  id,
  initialValues,
  isSubmitting,
  onSubmit,
}: ExpenseCategoryFormProps): JSX.Element => {
  const [values, setValues] =
    useState<ExpenseCategoryFormValues>(initialValues);

  const set = <K extends keyof ExpenseCategoryFormValues>(
    key: K,
    v: ExpenseCategoryFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  // Mirror the legacy form: enabling "use resource rate" disables the amount and
  // markup inputs and forces the expense to optional.
  const handleUseResourceRateToggle = (useRR: boolean) =>
    setValues((prev) => ({
      ...prev,
      useResourceRate: useRR,
      status: useRR ? "optional" : prev.status,
    }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-sm-6">
          <Form.Label htmlFor="expense-category-name">Name</Form.Label>
          <TextField
            label={null}
            id="expense-category-name"
            value={values.name}
            placeholder="Airfare, Meals (Per Diem), etc"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("name", e.target.value)
            }
            required
          />
        </div>
        <div className="col-sm-3">
          <DropdownApi
            label="Phase"
            value={toOption(values.phaseId, null)}
            onChange={(opt: any) =>
              set("phaseId", opt ? String(opt.value) : null)
            }
            getDataHook={useV2ListPhasesForAccountQuery}
            placeholder="Any phase"
          />
        </div>
        <div className="col-sm-3">
          <RequiredReactSelect
            label="Required/Optional"
            value={STATUS_OPTIONS.find((o) => o.value === values.status)}
            onChange={(opt: any) =>
              set("status", (opt?.value ?? "optional") as ExpenseCategoryStatus)
            }
            options={STATUS_OPTIONS}
            isDisabled={values.useResourceRate}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-sm-3">
          <FormNumberFieldV2
            label="Amount"
            id="expense-category-rate"
            value={values.rate}
            step={0.01}
            disabled={values.useResourceRate}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("rate", e.target.value === "" ? null : Number(e.target.value))
            }
            fieldFooter="Leave blank to allow override"
          />
        </div>
        <div className="col-sm-3">
          <FormNumberFieldV2
            label="Markup (%)"
            id="expense-category-markup"
            value={values.markup}
            min={0}
            step={0.01}
            disabled={values.useResourceRate}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set(
                "markup",
                e.target.value === "" ? null : Number(e.target.value)
              )
            }
            fieldFooter="Used when adding this expense to project revenue"
          />
        </div>
        <div className="col-sm-3">
          <Form.Label>Use Resource Travel/Expense Rate</Form.Label>
          <div className="expenseCategoryUseResourceRateToggle">
            <GenericToggle
              key={`use-rr-${initialValues.useResourceRate}`}
              initialValueOfIsOn={values.useResourceRate}
              onToggle={handleUseResourceRateToggle}
              onText="Use resource rate"
              offText="Set own rate"
              hasText
            />
          </div>
          <small className="small text-muted d-block">
            Check this to use the Resource Travel/Expense rate. If checked, the
            expense becomes optional.
          </small>
        </div>
      </div>
    </Form>
  );
};

export default ExpenseCategoryForm;
